import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell, X } from 'lucide-react'
import { useI18n } from '../i18n/useI18n'
import { useStore } from '../store/useStore'
import type { Store } from '../store/useStore'
import { api } from '../services/api'

interface BannerData {
  id: number
  title: string
  body: string
  orderId: string
  // #968 現金会計の承認通知。これがあると CTA が「レシート・レビューへ」になり、
  // 支払いコンテキストを張って /payment/complete へ（カードと同じフロー）。
  // #1397 orderIds = この会計で締められた注文。レシート導線がサーバ側の
  // 現金取引を特定するのに使う。
  review?: { storeId: string; amount: number; orderIds?: string[] }
}

let nextId = 0
let pushFn: ((b: Omit<BannerData, 'id'>) => void) | null = null

// eslint-disable-next-line react-refresh/only-export-components
export function showOrderReadyBanner(b: Omit<BannerData, 'id'>): void {
  pushFn?.(b)
}

/**
 * Top-mounted banner that announces when an order is marked `served`.
 *
 * Uses an imperative API (like Toast) so the notifier hook doesn't need
 * to thread callbacks through React context. One banner visible at a time;
 * a newer notification replaces the current one.
 */
export default function OrderReadyBanner() {
  const navigate = useNavigate()
  const { t } = useI18n()
  const stores = useStore((s) => s.stores)
  const markCashPaymentComplete = useStore((s) => s.markCashPaymentComplete)
  const [banner, setBanner] = useState<BannerData | null>(null)

  // #968 現金会計の承認通知 CTA: 支払いコンテキストを張って /payment/complete へ。
  const goToReceipt = async (review: { storeId: string; amount: number; orderIds?: string[] }) => {
    setBanner(null)
    let store = stores.find((s) => s.id === review.storeId)
    // #1397 /my-orders から直接来た客は店舗一覧を開いていないので stores が空。
    // ここで諦めると支払いコンテキストを張らないまま遷移してしまい、支払い完了
    // 画面が「¥0 / レシート導線なし」になる。取れなければ id だけの partial で進む
    // (CheckoutSheet と同じ扱い — 画面が読むのは id/name/category だけ)。
    if (!store) {
      try {
        store = await api.getStore(review.storeId) as Store
      } catch {
        store = { id: review.storeId, name: '', category: '' } as unknown as Store
      }
    }
    markCashPaymentComplete(store, review.amount, review.orderIds || [])
    navigate('/payment/complete')
  }

  useEffect(() => {
    pushFn = (b) => {
      const id = ++nextId
      setBanner({ ...b, id })
      window.setTimeout(() => {
        setBanner((curr) => (curr?.id === id ? null : curr))
      }, 10000)
    }
    return () => { pushFn = null }
  }, [])

  if (!banner) return null

  return (
    <div className="fixed inset-x-0 top-0 z-[110] flex justify-center px-3 pt-[max(0.5rem,env(safe-area-inset-top))] pointer-events-none">
      <div className="pointer-events-auto w-full max-w-[430px] animate-slide-up rounded-2xl border border-gold/50 bg-white shadow-xl">
        <div className="flex items-start gap-3 px-4 py-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gold/15">
            <Bell size={20} className="text-gold animate-pulse" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-display text-sm font-bold text-text-primary">{banner.title}</p>
            <p className="mt-0.5 font-body text-xs text-text-secondary leading-relaxed">
              {banner.body}
            </p>
          </div>
          <button
            onClick={() => setBanner(null)}
            className="shrink-0 rounded-full p-1 tap-effect hover:bg-gray-100"
            aria-label="close"
          >
            <X size={16} className="text-text-tertiary" />
          </button>
        </div>
        <div className="flex gap-2 border-t border-gray-100 px-3 py-2">
          <button
            onClick={() => setBanner(null)}
            className="flex-1 rounded-lg py-2 font-body text-sm text-text-secondary tap-effect hover:bg-gray-50"
          >
            {t('notify.dismiss')}
          </button>
          <button
            onClick={() => {
              if (banner.review) {
                void goToReceipt(banner.review)
              } else {
                setBanner(null)
                navigate('/my-orders')
              }
            }}
            className="flex-1 rounded-lg bg-navy py-2 font-display text-sm font-semibold text-white tap-effect hover:bg-navy-light"
          >
            {banner.review ? t('notify.viewReceipt') : t('notify.viewOrder')}
          </button>
        </div>
      </div>
    </div>
  )
}
